import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Button, Alert, Breadcrumbs, PanelCard, Spinner, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@glplus/glpweb-dsl';
import { useVendor } from '../../lib/CapabilitiesContext';
import { vendorApiClient, type UpdateLocationInput, type VendorLocation } from '../../lib/vendorApiClient';
import { normalizeWeek, hoursTodayLabel } from '../../lib/hours';
import { fromE164, toE164 } from '../../lib/phone';
import LocationStatusChip from '../../components/LocationStatusChip';
import { LocationDetailsCard, HoursCard, ServicesCard, SnapCard, ShopperPreviewCard, type LocationFormState } from './LocationForm';
import { weekToApi, describeError } from './LocationAdd';
import LocationRecipients from './LocationRecipients';

/**
 * L2 — Location detail / edit (Figma 9:44) and L5 — Needs changes (12:359),
 * one screen keyed on `status`. Saving PATCHes only what changed; an address
 * change on a live location sends it back to `verifying` (vendor-api re-geocodes
 * and re-queues review). Delete = retire (`permanently_closed`, prices kept).
 */

const HEADING = '#303A42';
const MUTED = '#5A7063';

function toForm(l: VendorLocation): LocationFormState {
  return {
    name: l.name,
    line1: l.address.line1,
    line2: l.address.line2 ?? '',
    city: l.address.city,
    state: l.address.state,
    postalCode: l.address.postalCode,
    phone: fromE164(l.phoneE164),
    website: l.website ?? '',
    hours: normalizeWeek(l.hours),
    pickupSupported: l.pickupSupported,
    temporarilyClosed: l.temporarilyClosed,
    fnsNumber: l.fnsNumber ?? '',
  };
}

export default function LocationDetail() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { storeId = '' } = useParams();
  const { activeVendorId, activeMembership, has } = useVendor();
  const canManage = has('location:manage');

  const [loc, setLoc] = useState<VendorLocation | null>(null);
  const [form, setForm] = useState<LocationFormState | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmRetire, setConfirmRetire] = useState(false);
  const [archiveReason, setArchiveReason] = useState('');

  const load = useCallback(() => {
    if (!activeVendorId || !storeId) return;
    setLoadError(null);
    vendorApiClient
      .getLocation(activeVendorId, storeId)
      .then((l) => {
        setLoc(l);
        setForm(toForm(l));
      })
      .catch((e: Error) => setLoadError(e.message));
  }, [activeVendorId, storeId]);

  useEffect(load, [load]);

  const baseline = useMemo(() => (loc ? toForm(loc) : null), [loc]);
  const addressChanged = !!(
    form &&
    baseline &&
    (form.line1 !== baseline.line1 || form.line2 !== baseline.line2 || form.city !== baseline.city || form.state !== baseline.state || form.postalCode !== baseline.postalCode)
  );
  const dirty = !!(form && baseline && JSON.stringify(form) !== JSON.stringify(baseline));

  if (loadError) return <Alert severity="error">{loadError}</Alert>;
  if (!loc || !form || !baseline) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <Spinner color="brand" />
      </Box>
    );
  }

  const readOnly = !canManage || busy;
  const patch = (p: Partial<LocationFormState>) => setForm((f) => (f ? { ...f, ...p } : f));

  const buildPatch = (): UpdateLocationInput | string => {
    const out: UpdateLocationInput = {};
    if (form.name.trim() !== baseline.name) {
      if (!form.name.trim()) return t('locations.errors.nameRequired');
      out.name = form.name.trim();
    }
    if (addressChanged) {
      if (!form.line1.trim() || !form.city.trim() || !form.state.trim() || !form.postalCode.trim()) return t('locations.errors.addressRequired');
      out.address = {
        line1: form.line1.trim(),
        line2: form.line2.trim() || undefined,
        city: form.city.trim(),
        state: form.state.trim().toUpperCase(),
        postalCode: form.postalCode.trim(),
      };
    }
    if (form.phone !== baseline.phone) {
      const e164 = toE164(form.phone);
      if (form.phone.trim() && !e164) return t('locations.errors.phoneInvalid');
      out.phoneE164 = e164;
    }
    if (form.website !== baseline.website) out.website = form.website.trim() || null;
    if (JSON.stringify(form.hours) !== JSON.stringify(baseline.hours)) out.hours = weekToApi(form.hours);
    if (form.pickupSupported !== baseline.pickupSupported) out.pickupSupported = form.pickupSupported;
    if (form.temporarilyClosed !== baseline.temporarilyClosed) out.temporarilyClosed = form.temporarilyClosed;
    if (form.fnsNumber !== baseline.fnsNumber) {
      if (form.fnsNumber && form.fnsNumber.length !== 7) return t('locations.errors.fnsInvalid');
      out.fnsNumber = form.fnsNumber || null;
    }
    return out;
  };

  const run = async (work: () => Promise<VendorLocation | null>, done: string) => {
    if (!activeVendorId) return;
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      const next = await work();
      if (next) {
        setLoc(next);
        setForm(toForm(next));
      }
      setNotice(done);
    } catch (e) {
      setError(describeError(e, t));
    } finally {
      setBusy(false);
    }
  };

  const save = () => {
    const p = buildPatch();
    if (typeof p === 'string') {
      setError(p);
      return;
    }
    void run(() => vendorApiClient.updateLocation(activeVendorId!, storeId, p), t('locations.detail.saved'));
  };

  const resubmit = () => {
    // Unsaved edits ride along: save first, then flip the status.
    const p = buildPatch();
    if (typeof p === 'string') {
      setError(p);
      return;
    }
    void run(async () => {
      if (Object.keys(p).length) await vendorApiClient.updateLocation(activeVendorId!, storeId, p);
      return vendorApiClient.resubmitLocation(activeVendorId!, storeId);
    }, t('locations.detail.resubmitted'));
  };

  const retire = async () => {
    if (!activeVendorId) return;
    setConfirmRetire(false);
    setBusy(true);
    try {
      await vendorApiClient.retireLocation(activeVendorId, storeId, archiveReason.trim() || undefined);
      navigate('/locations', { replace: true });
    } catch (e) {
      setError(describeError(e, t));
      setBusy(false);
    }
  };

  const today = hoursTodayLabel(form.hours);
  const hoursText = today.open ? `${t('locations.openToday')} · ${today.text}` : t('locations.closedToday');

  return (
    <Box>
      <Breadcrumbs
        ariaLabel={t('locations.breadcrumbAria')}
        linkComponent={Link}
        items={[
          { label: t('nav.locations'), to: '/locations' },
          { label: loc.name, ariaCurrent: true },
        ]}
      />
      <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Typography component="h1" sx={{ fontSize: 40, lineHeight: 1.2, fontWeight: 700, color: HEADING }}>
          {loc.name}
        </Typography>
        <LocationStatusChip status={loc.status} size="medium" />
      </Box>
      <Typography sx={{ mt: 0.5, fontSize: 16, color: MUTED }}>{loc.address.formatted}</Typography>

      {loc.status === 'needs_changes' && (
        <Alert severity="error" title={t('locations.detail.needsChangesTitle')} sx={{ mt: 2 }}>
          <Typography sx={{ fontSize: 14, mb: 1 }}>{loc.statusNote || t('locations.detail.needsChangesNoNote')}</Typography>
          <Typography sx={{ fontSize: 13, color: MUTED }}>{t('locations.detail.needsChangesHint')}</Typography>
        </Alert>
      )}
      {loc.status === 'verifying' && (
        <Alert severity="warning" title={t('locations.detail.verifyingTitle')} sx={{ mt: 2 }}>
          {t('locations.detail.verifyingBody')}
        </Alert>
      )}
      {loc.status === 'live' && addressChanged && (
        <Alert severity="info" sx={{ mt: 2 }}>
          {t('locations.detail.addressReverify')}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" sx={{ mt: 2 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}
      {!canManage && (
        <Alert severity="info" sx={{ mt: 2 }}>
          {t('locations.detail.viewOnly')}
        </Alert>
      )}

      <Box sx={{ mt: 3, display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 320px' }, gap: 3, alignItems: 'start' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
          <PanelCard title={t('locations.checklist.title', { location: loc.name })} subtitle={t('locations.checklist.subtitle')} accent={loc.setupComplete ? '#25862D' : '#F67A1A'} sx={{ height: 'auto', bgcolor: loc.setupComplete ? '#EEF7EF' : '#FFF4E8' }} headingLevel="h2">
            <Typography sx={{ fontWeight: 700, mb: 1 }}>{loc.setupComplete ? t('locations.checklist.complete') : t('locations.checklist.progress', { done: Object.values(loc.launchChecklist).filter(Boolean).length, total: 5 })}</Typography>
            {(['profileAddress', 'hours', 'compliance', 'fulfillment', 'products'] as const).map((key) => <Box key={key} sx={{ display: 'flex', gap: 1, py: 0.5, bgcolor: '#fff' }}><Typography sx={{ color: loc.launchChecklist[key] ? '#25862D' : '#A85200', fontWeight: 800 }}>{loc.launchChecklist[key] ? '✓' : '○'}</Typography><Typography>{t(`locations.checklist.items.${key}`)}</Typography></Box>)}
            <Typography sx={{ color: 'text.secondary', fontSize: 13, mt: 1 }}>{t('locations.checklist.delivery')}</Typography>
          </PanelCard>
          <LocationDetailsCard form={form} onChange={patch} disabled={readOnly} />
          <HoursCard hours={form.hours} onChange={(hours) => patch({ hours })} disabled={readOnly} />
          <ServicesCard form={form} onChange={patch} disabled={readOnly} />
          <SnapCard form={form} onChange={patch} verification={loc.fnsVerification} verifiedAt={loc.fnsVerifiedAt} disabled={readOnly} />

          {canManage && (
            <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              {dirty && <Button ariaLabel={t('common.cancel')} buttonText={t('common.cancel')} variant="outlined" onClick={() => setForm(baseline)} disabled={busy} />}
              {loc.status === 'needs_changes' ? (
                <Button ariaLabel={t('locations.detail.resubmit')} buttonText={busy ? t('common.saving') : t('locations.detail.resubmit')} variant="contained" color="primary" onClick={resubmit} disabled={busy} />
              ) : (
                <Button ariaLabel={t('common.saveChanges')} buttonText={busy ? t('common.saving') : t('common.saveChanges')} variant="contained" color="primary" onClick={save} disabled={busy || !dirty} />
              )}
            </Box>
          )}

          {activeVendorId && <LocationRecipients vendorId={activeVendorId} storeId={storeId} canManage={canManage} />}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <ShopperPreviewCard
            name={form.name}
            businessType={activeMembership?.businessName ?? ''}
            hoursText={hoursText}
            snap={loc.snapAccepted}
            pickup={form.pickupSupported}
            live={loc.status === 'live'}
          />

          <PanelCard title={t('locations.detail.statsTitle')} sx={{ height: 'auto', bgcolor: '#fff' }} headingLevel="h3">
            <Stat label={t('locations.detail.productsPriced')} value={loc.productsPriced ?? 0} />
            <Stat label={t('locations.detail.onSaleNow')} value={loc.onSaleNow ?? 0} />
            <Typography sx={{ mt: 1.5, fontSize: 12, color: MUTED }}>
              {loc.status === 'live' ? t('locations.detail.statsLiveHint') : t('locations.detail.statsPendingHint')}
            </Typography>
          </PanelCard>

          {canManage && (
            <PanelCard title={t('locations.detail.dangerTitle')} sx={{ height: 'auto', bgcolor: '#fff' }} headingLevel="h3">
              <Typography sx={{ fontSize: 13, color: MUTED, mb: 1.5 }}>{t('locations.detail.dangerBody')}</Typography>
              <Button ariaLabel={t('locations.detail.delete')} buttonText={t('locations.detail.delete')} variant="outlined" color="error" onClick={() => setConfirmRetire(true)} disabled={busy} />
            </PanelCard>
          )}
        </Box>
      </Box>

      <Dialog open={confirmRetire} onClose={() => setConfirmRetire(false)} maxWidth="xs" ariaLabelledBy="retire-title">
        <DialogTitle id="retire-title">{t('locations.detail.deleteConfirmTitle', { name: loc.name })}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14 }}>{t('locations.detail.deleteConfirmBody')}</Typography>
          <TextField ariaLabel={t('locations.archives.reason')} label={t('locations.archives.reason')} value={archiveReason} onChange={(event) => setArchiveReason(event.target.value)} multiline minRows={3} fullWidth sx={{ mt: 2 }} />
        </DialogContent>
        <DialogActions>
          <Button ariaLabel={t('common.cancel')} buttonText={t('common.cancel')} variant="text" onClick={() => setConfirmRetire(false)} />
          <Button ariaLabel={t('locations.detail.delete')} buttonText={t('locations.detail.delete')} variant="contained" color="error" onClick={() => void retire()} />
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', py: 0.75, borderBottom: '1px solid #F0F0F0' }}>
      <Typography sx={{ fontSize: 14, color: '#343434' }}>{label}</Typography>
      <Typography sx={{ fontSize: 20, fontWeight: 700, color: HEADING }}>{value}</Typography>
    </Box>
  );
}
